/**
 * The Scope section at the top of Step 1 in the AUTOMATIONS_HUB entry point:
 * a "Scope" label with an "Edit scope" button on the same row, and the selected
 * scope rendered as a two-line block (name on top, kind label below) that
 * mirrors the table cell and view-trigger drawer. Clicking "Edit scope" opens
 * the DrawerPageEditScope sub-view.
 */

import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {TriggerScopeType} from '../../../../generated/graphql';
import {useAutomationsOrgScope} from '../../../Automations/hooks/useAutomationsOrgScope';
import {checkRegistryProject} from '../../../Registries/common/utils';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {AutomationStoreActions} from './AutomationStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {HubProjectPicker} from './DrawerPageEditScope';
import {EventStoreActions, useEventStore} from './EventStore';
import {EntryPoint} from './types';

const SCOPE_KIND_LABEL: Record<EntryPoint, string> = {
  [EntryPoint.ORGANIZATION]: 'Organization',
  [EntryPoint.TEAM]: 'Team',
  [EntryPoint.PROJECT]: 'Project',
  [EntryPoint.REGISTRY]: 'Registry',
  [EntryPoint.PORTFOLIO]: 'Registry collection',
  [EntryPoint.TEAM_MODEL_REGISTRY]: 'Team model registry',
  [EntryPoint.WORKSPACE_PANEL]: 'Workspace panel',
  [EntryPoint.AUTOMATIONS_HUB]: 'Automations hub',
};

/**
 * Render the picked scope as "<entityName>/<projectName>", the bare registry
 * name for a registry, or "<projectName|registryName>/<collectionName>" when
 * the scope is a collection.
 */
function getScopeDisplayName(eventConfig: {
  automationsHubScopeKind: EntryPoint | undefined;
  entityName: string | undefined;
  projectName: string | undefined;
  scopeArtifactCollectionName: string | undefined;
}): string | undefined {
  const {
    automationsHubScopeKind,
    entityName,
    projectName,
    scopeArtifactCollectionName,
  } = eventConfig;
  if (automationsHubScopeKind == null) {
    return undefined;
  }

  // Org- and team-wide scopes display the entity name directly (no project).
  if (
    automationsHubScopeKind === EntryPoint.ORGANIZATION ||
    automationsHubScopeKind === EntryPoint.TEAM
  ) {
    return entityName;
  }

  const {isRegistryProject, registryName} = checkRegistryProject(projectName);

  if (automationsHubScopeKind === EntryPoint.PORTFOLIO) {
    if (projectName && scopeArtifactCollectionName) {
      return `${isRegistryProject ? registryName : projectName}/${scopeArtifactCollectionName}`;
    }
    return undefined;
  }
  if (automationsHubScopeKind === EntryPoint.REGISTRY) {
    // Registries are named like the hub table's Scope cell: the bare registry
    // name, not `entity/wandb-registry-<name>`.
    if (projectName) {
      return registryName ?? projectName;
    }
    return undefined;
  }
  if (entityName && projectName) {
    return `${entityName}/${projectName}`;
  }
  return undefined;
}

export const HubScopeRow = () => {
  const {
    automationsHubScopeKind,
    entityName,
    projectName,
    scopeArtifactCollectionName,
  } = useEventStore(
    useShallow(s => ({
      automationsHubScopeKind: s.automationsHubScopeKind,
      entityName: s.entityName,
      projectName: s.projectName,
      scopeArtifactCollectionName: s.scopeArtifactCollectionName,
    }))
  );

  const scopeKindLabel =
    automationsHubScopeKind != null
      ? SCOPE_KIND_LABEL[automationsHubScopeKind]
      : null;
  // EventStore carries the org *entity* name for an organization scope (the
  // API works in entity terms); users expect the organization name here.
  const {orgName} = useAutomationsOrgScope();
  const scopeName = getScopeDisplayName({
    automationsHubScopeKind,
    entityName:
      automationsHubScopeKind === EntryPoint.ORGANIZATION
        ? orgName || entityName
        : entityName,
    projectName,
    scopeArtifactCollectionName,
  });

  return (
    <Tailwind>
      <S.LabelRow className="justify-between gap-8">
        <S.Label>Scope</S.Label>
        <Button
          variant="secondary"
          size="small"
          icon="pencil-edit"
          onClick={() => AutomationStoreActions.openEditScope()}>
          Edit scope
        </Button>
      </S.LabelRow>
      <div className="min-w-0 w-full">
        {scopeName != null ? (
          <>
            <div
              className="truncate font-semibold text-moon-600 dark:text-wb-text-secondary"
              title={scopeName}>
              {scopeName}
            </div>
            {scopeKindLabel != null && (
              <div
                className="truncate text-sm text-moon-500 dark:text-wb-text-tertiary"
                title={scopeKindLabel}>
                {scopeKindLabel}
              </div>
            )}
          </>
        ) : (
          <span className="text-moon-500 dark:text-wb-text-tertiary">
            No scope selected
          </span>
        )}
      </div>
    </Tailwind>
  );
};

/**
 * Stands in for HubScopeRow when the event allows only project scope, as on
 * the sandboxes page: there is no scope kind to choose, so the user picks the
 * project inline instead of through the Edit scope sub-view.
 */
export const HubProjectRow = () => {
  const {entityName, projectID, projectName} = useEventStore(
    useShallow(s => ({
      entityName: s.entityName,
      projectID: s.projectID,
      projectName: s.projectName,
    }))
  );
  const {selectedAccount} = useContext(AccountSelectorContext);

  return (
    <Tailwind>
      <HubProjectPicker
        orgID={selectedAccount?.id ?? ''}
        pending={{entityName, projectID, projectName}}
        onPick={picked =>
          EventStoreActions.setHubScope({
            scopeKind: EntryPoint.PROJECT,
            scopeType: TriggerScopeType.Project,
            scopeID: picked.projectID,
            ...picked,
          })
        }
      />
    </Tailwind>
  );
};
